import { useOrderPanelStore } from '../../stores/order-panel-store'
import { VloerkleedConfigurator } from './VloerkleedConfigurator'
import { PriceSummary } from './PriceSummary'
import { DiscountControl } from './DiscountControl'
import { RouteSelector } from './RouteSelector'
import { OrderCapacityBumpCard } from './OrderCapacityBumpCard'
import { ToeslagControl } from '../nieuwe-aanvraag/ToeslagControl'
import clsx from 'clsx'

/**
 * Layout-agnostische binnenkant van de order-creatie — store-driven via
 * useOrderPanelStore.
 *
 * - variant 'drawer' (klant-detail): volledige verticale stapel incl.
 *   channel-toggle en PriceSummary (gedrag identiek aan het oude OrderPanel).
 * - variant 'page' (/nieuwe-aanvraag): zonder channel-toggle (AfhandelingBlok
 *   stuurt het channel) en zonder PriceSummary (de pagina plaatst die zelf).
 *   De pagina rendert de secties los, volgens het telefoonscript:
 *   `sections='configurator'` (kleden + korting — prijs noemen) eerst, en
 *   `sections='planning'` (logistiek/route/opmerkingen/bevestigingen) pas
 *   nadat de klant voor "Direct inboeken" koos.
 */
export function OrderComposer({
  variant,
  sections = 'all',
}: {
  variant: 'drawer' | 'page'
  sections?: 'all' | 'configurator' | 'planning'
}) {
  const isLoadingBootstrap = useOrderPanelStore((s) => s.isLoadingBootstrap)
  const bootstrapError = useOrderPanelStore((s) => s.bootstrapError)
  const bootstrap = useOrderPanelStore((s) => s.bootstrap)
  const buitenWerkgebiedDetected = useOrderPanelStore((s) => s.buitenWerkgebiedDetected)
  const geenAdresDetected = useOrderPanelStore((s) => s.geenAdresDetected)
  const wasserijFallback = useOrderPanelStore((s) => s.wasserijFallback)
  const customerPostcode = useOrderPanelStore((s) => s.customer?.postcode)
  const maakBuitenWerkgebiedUitzondering = useOrderPanelStore((s) => s.maakBuitenWerkgebiedUitzondering)
  const maakZelfBrengenZonderAdres = useOrderPanelStore((s) => s.maakZelfBrengenZonderAdres)
  const channel = useOrderPanelStore((s) => s.channel)
  const opmerkingenPlanning = useOrderPanelStore((s) => s.opmerkingenPlanning)
  const opmerkingenOphalen = useOrderPanelStore((s) => s.opmerkingenOphalen)
  const zelfServiceWalkIn = useOrderPanelStore((s) => s.zelfServiceWalkIn)
  const zelfHalen = useOrderPanelStore((s) => s.zelfHalen)
  const zelfBrengen = useOrderPanelStore((s) => s.zelfBrengen)
  const bevestigingenSturen = useOrderPanelStore((s) => s.bevestigingenSturen)
  const setChannel = useOrderPanelStore((s) => s.setChannel)
  const setOpmerkingenPlanning = useOrderPanelStore((s) => s.setOpmerkingenPlanning)
  const setOpmerkingenOphalen = useOrderPanelStore((s) => s.setOpmerkingenOphalen)
  const setZelfHalen = useOrderPanelStore((s) => s.setZelfHalen)
  const setZelfBrengen = useOrderPanelStore((s) => s.setZelfBrengen)
  const setBevestigingenSturen = useOrderPanelStore((s) => s.setBevestigingenSturen)

  const showChannelToggle = variant === 'drawer'
  const showPriceSummary = variant === 'drawer'
  // Drawer: ongewijzigd gedrag (logistiek/bevestigingen ook zichtbaar bij QUOTE).
  // Page: de planning-sectie wordt sowieso alleen in het inboeken-pad gerenderd.
  const showLogistiek = variant === 'drawer' || channel === 'ORDER'

  const renderConfigurator = sections !== 'planning'
  const renderPlanning = sections !== 'configurator'
  // Loading/error tonen we één keer — in de configurator-sectie (de losse
  // planning-sectie wordt pas gerenderd als de bootstrap er al is).
  const showLoadStates = renderConfigurator

  return (
    <>
      {/* Bootstrap loading */}
      {showLoadStates && isLoadingBootstrap && (
        <div className="space-y-3 animate-pulse">
          <div className="h-4 bg-slate-200 rounded w-40" />
          <div className="h-20 bg-slate-100 rounded" />
          <div className="h-20 bg-slate-100 rounded" />
        </div>
      )}

      {/* Bootstrap error */}
      {showLoadStates && bootstrapError && (
        <div className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {bootstrapError}
        </div>
      )}

      {/* Geen adres bekend — zelf brengen/halen aanbieden */}
      {showLoadStates && geenAdresDetected && !isLoadingBootstrap && (
        <div className="px-3 py-3 bg-amber-50 border border-amber-200 rounded-md space-y-2">
          <p className="text-sm font-medium text-amber-800">
            Geen adres bekend op deze klant
          </p>
          <p className="text-xs text-amber-800">
            Zonder postcode kunnen er geen prijzen of ophaalroutes bepaald worden.
            Brengt en haalt de klant het kleed zelf in Havelte? Dan gelden de
            standaard wasserij-prijzen zonder regiotoeslag en worden er geen
            ritten aangemaakt. Wil je wel laten ophalen, vul dan eerst het adres
            in op de klantkaart.
          </p>
          <button
            type="button"
            onClick={maakZelfBrengenZonderAdres}
            className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
          >
            Zelf brengen en halen — prijzen laden
          </button>
        </div>
      )}

      {/* Postcode buiten werkgebied — uitzondering aanbieden */}
      {showLoadStates && buitenWerkgebiedDetected && !isLoadingBootstrap && (
        <div className="px-3 py-3 bg-amber-50 border border-amber-200 rounded-md space-y-2">
          <p className="text-sm font-medium text-amber-800">
            Postcode {customerPostcode} valt buiten het werkgebied
          </p>
          <p className="text-xs text-amber-800">
            Er is geen dienstgebied voor deze postcode. Je kunt een uitzondering
            maken: de prijs krijgt de buiten-werkgebied toeslag (daarna aanpasbaar
            op afstand/opdracht) en de order wordt na aanmaak handmatig ingepland.
          </p>
          <button
            type="button"
            onClick={maakBuitenWerkgebiedUitzondering}
            className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
          >
            Uitzondering maken — prijzen met toeslag laden
          </button>
        </div>
      )}

      {/* Main content (only when bootstrap loaded) */}
      {bootstrap && (
        <>
          {renderConfigurator && (
            <>
              {/* Buiten-werkgebied toeslag (alleen zichtbaar in die modus) */}
              <ToeslagControl />

              {/* Configurator (product-keuze leeft per-item) */}
              <VloerkleedConfigurator />

              {/* Channel */}
              {showChannelToggle && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-600 block">Type</label>
                  <div className="flex gap-2">
                    {(['ORDER', 'QUOTE'] as const).map((ch) => (
                      <button
                        key={ch}
                        onClick={() => setChannel(ch)}
                        className={clsx(
                          'flex-1 px-3 py-2 text-sm rounded-md border transition-colors',
                          channel === ch
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-medium'
                            : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        )}
                      >
                        {ch === 'ORDER' ? 'Order' : 'Offerte'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {renderPlanning && (
            <>
              {/* Logistiek */}
              {showLogistiek && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-600 block">Logistiek</label>
                  <div className="space-y-1.5">
                    {/* Wasserij-fallback (geen adres): prijzen gelden alleen voor
                        zelf brengen/halen — vinkjes staan vast tot er een adres is. */}
                    <label className={clsx('flex items-center gap-2 text-sm', wasserijFallback ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}>
                      <input
                        type="checkbox"
                        checked={zelfBrengen}
                        disabled={wasserijFallback}
                        onChange={(e) => setZelfBrengen(e.target.checked)}
                        className="rounded border-slate-300"
                      />
                      <span>Zelf brengen — klant brengt kleed naar Havelte</span>
                    </label>
                    <label className={clsx('flex items-center gap-2 text-sm', wasserijFallback ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}>
                      <input
                        type="checkbox"
                        checked={zelfHalen}
                        disabled={wasserijFallback}
                        onChange={(e) => setZelfHalen(e.target.checked)}
                        className="rounded border-slate-300"
                      />
                      <span>Zelf halen — klant haalt kleed op na reiniging</span>
                    </label>
                    {wasserijFallback && (
                      <p className="text-xs text-slate-500">
                        {zelfServiceWalkIn
                          ? 'Walk-in: standaard wasserij-prijzen zonder regiotoeslag, geen ritten. Moet er tóch opgehaald of bezorgd worden, start dan opnieuw met de postcode van de klant.'
                          : 'Geen adres bekend — ophalen of bezorgen kan pas nadat het adres op de klantkaart is ingevuld.'}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Route */}
              <RouteSelector />

              {/* Capacity bump (deferred — uitgevoerd ná Lead-creatie met leadId-anchor) */}
              <OrderCapacityBumpCard />
            </>
          )}

          {/* Discount — bij de prijs (configurator-sectie): prijs incl. korting noemen */}
          {renderConfigurator && <DiscountControl />}

          {renderPlanning && (
            <>
              {/* Opmerkingen ophalen */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-slate-600 block">Opmerkingen ophalen</label>
                <input
                  type="text"
                  placeholder="Bijv. bel 10 min van tevoren..."
                  value={opmerkingenOphalen}
                  onChange={(e) => setOpmerkingenOphalen(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Opmerkingen planning */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-slate-600 block">Opmerkingen planning</label>
                <textarea
                  placeholder="Bijv. route-voorkeur, toegang, parkeren..."
                  value={opmerkingenPlanning}
                  onChange={(e) => setOpmerkingenPlanning(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Bevestigingen */}
              {showLogistiek && (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={bevestigingenSturen}
                      onChange={(e) => setBevestigingenSturen(e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    <span className="font-medium">Bevestigingen direct sturen</span>
                  </label>
                  {!bevestigingenSturen && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                      Geen S01/S03-mail bij creatie. Activeer later vanaf de order-pagina.
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* Price Summary */}
          {showPriceSummary && <PriceSummary />}
        </>
      )}
    </>
  )
}
